/**
 * InviteUserForm Component
 *
 * Form for inviting users to a tenant with:
 * - Entra ID user search (autocomplete from directory)
 * - Manual email fallback
 * - Role selector
 * - Submit button with loading state
 *
 * Pattern: `react-hook-form` + `zodResolver`, schema imported from `InviteUser.schema.ts` (never
 * redefined), server errors mapped via `setError` (Charter M3). The directory-search
 * combobox (query/results/dropdown-open) is bespoke UI state — not itself part of
 * `CreateInvitationRequest` — that resolves into the RHF-managed `email` field on selection or
 * manual typing (form-standard.context.md §12, "custom forms still follow this standard"). The
 * outer `Dialog` chrome is owned by `IdentityPage.tsx` / `UsersRolesPage.tsx` (`src/pages/**`) —
 * this component renders the form CONTENT only and keeps its existing `tenantId`/`onSuccess`/
 * `onCancel` prop contract so those pages need no change.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Input,
  Label,
  Select,
  Spinner,
  Body1,
  Body2,
  Caption1,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { SendRegular, SearchRegular, PersonRegular } from '@fluentui/react-icons';
import { useCreateInvitation } from '../hooks';
import { TenantRole } from '../types';
import { getAvailableRoles, searchEntraIdUsers, type EntraIdUser } from '../services';
import { applyServerErrors, useReportFormDirty, FormActions, ServerErrorBanner } from '../../../components/forms';
import { InviteUserSchema, type InviteUserFormValues, type InviteUserFormOutput } from './InviteUser.schema';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    maxWidth: '480px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  searchContainer: {
    position: 'relative',
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 1000,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
    maxHeight: '240px',
    overflowY: 'auto',
    marginTop: '2px',
  },
  dropdownItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  dropdownItemInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
    flex: 1,
  },
  dropdownItemName: {
    fontWeight: tokens.fontWeightSemibold,
  },
  selectedUser: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  selectedUserInfo: {
    flex: 1,
    minWidth: 0,
  },
  selectedUserName: {
    fontWeight: tokens.fontWeightSemibold,
  },
  mutedText: {
    color: tokens.colorNeutralForeground3,
  },
  mutedBlock: {
    color: tokens.colorNeutralForeground3,
    display: 'block',
  },
});

// Known request-DTO field keys — used by the server-error -> field mapping.
const KNOWN_FIELDS = ['email', 'role'] as const;

const DEFAULT_VALUES: InviteUserFormValues = {
  email: '',
  role: TenantRole.Viewer,
};

export interface InviteUserFormProps {
  /** The tenant ID to send the invitation for */
  tenantId: string;
  /** Callback when invitation is successfully sent */
  onSuccess?: () => void;
  /** Callback when form is cancelled */
  onCancel?: () => void;
}

export const InviteUserForm: FC<InviteUserFormProps> = ({ tenantId, onSuccess, onCancel }) => {
  const styles = useStyles();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<EntraIdUser | null>(null);
  const [searchResults, setSearchResults] = useState<EntraIdUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [globalError, setGlobalError] = useState<string | undefined>();
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const createInvitation = useCreateInvitation();

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<InviteUserFormValues, unknown, InviteUserFormOutput>({
    resolver: zodResolver(InviteUserSchema),
    mode: 'onTouched',
    defaultValues: DEFAULT_VALUES,
  });

  useReportFormDirty(isDirty, onDirtyChangeNoop);

  const emailValue = watch('email');

  // Close dropdown when clicking outside — unrelated to form validation, kept as-is.
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced Entra ID search
  const handleSearchChange = useCallback(
    (query: string) => {
      setSearchQuery(query);
      setSelectedUser(null);
      setValue('email', query, { shouldDirty: true }); // Allow manual email entry as fallback

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      if (query.length < 2) {
        setSearchResults([]);
        setShowDropdown(false);
        return;
      }

      searchTimeoutRef.current = setTimeout(async () => {
        setIsSearching(true);
        try {
          const results = await searchEntraIdUsers(query);
          setSearchResults(results);
          setShowDropdown(results.length > 0);
        } catch {
          setSearchResults([]);
          setShowDropdown(false);
        } finally {
          setIsSearching(false);
        }
      }, 300);
    },
    [setValue]
  );

  const handleSelectUser = (user: EntraIdUser) => {
    setSelectedUser(user);
    setValue('email', user.mail || user.userPrincipalName, { shouldDirty: true, shouldValidate: true });
    setSearchQuery(user.displayName);
    setShowDropdown(false);
    setSearchResults([]);
  };

  const handleClearSelection = () => {
    setSelectedUser(null);
    setValue('email', '', { shouldDirty: true });
    setSearchQuery('');
  };

  const registerInvitation = useCallback(
    (values: InviteUserFormOutput) => {
      setGlobalError(undefined);
      createInvitation.mutate(
        { tenantId, email: values.email, role: values.role },
        {
          onSuccess: () => {
            reset(DEFAULT_VALUES);
            setSearchQuery('');
            setSelectedUser(null);
            onSuccess?.();
          },
          onError: (error) => {
            const { globalMessage } = applyServerErrors(error, KNOWN_FIELDS, setError);
            setGlobalError(globalMessage);
          },
        }
      );
    },
    [createInvitation, tenantId, reset, onSuccess, setError]
  );

  const roles = getAvailableRoles();

  return (
    <form onSubmit={handleSubmit(registerInvitation)} className={styles.form} noValidate>
      <ServerErrorBanner
        title="Error"
        message={globalError ?? errors.email?.message}
        testId="invite-user-form-server-error"
      />

      {createInvitation.isSuccess && (
        <ServerErrorBanner
          title="Invitation Sent"
          message={`An invitation has been sent to ${emailValue}.`}
          testId="invite-user-form-success"
          intent="success"
        />
      )}

      <div className={styles.field}>
        <Label htmlFor="user-search" required>
          Search Directory
        </Label>
        {selectedUser ? (
          <div className={styles.selectedUser} data-testid="invite-user-form-selected-user">
            <PersonRegular />
            <div className={styles.selectedUserInfo}>
              <Body1 className={styles.selectedUserName}>{selectedUser.displayName}</Body1>
              <Caption1 className={styles.mutedBlock}>{selectedUser.mail || selectedUser.userPrincipalName}</Caption1>
            </div>
            <Button
              appearance="subtle"
              size="small"
              onClick={handleClearSelection}
              disabled={createInvitation.isPending}
            >
              Change
            </Button>
          </div>
        ) : (
          <div className={styles.searchContainer} ref={dropdownRef}>
            <Controller
              name="email"
              control={control}
              render={() => (
                <Input
                  id="user-search"
                  data-testid="invite-user-form-email"
                  value={searchQuery}
                  onChange={(_, data) => handleSearchChange(data.value)}
                  placeholder="Search by name or email..."
                  disabled={createInvitation.isPending}
                  contentBefore={isSearching ? <Spinner size="tiny" /> : <SearchRegular />}
                />
              )}
            />
            {showDropdown && (
              <div className={styles.dropdown}>
                {searchResults.map((user) => (
                  <div key={user.id} className={styles.dropdownItem} onClick={() => handleSelectUser(user)}>
                    <PersonRegular />
                    <div className={styles.dropdownItemInfo}>
                      <Body2 className={styles.dropdownItemName}>{user.displayName}</Body2>
                      <Caption1 className={styles.mutedText}>
                        {user.mail || user.userPrincipalName}
                        {user.jobTitle && ` • ${user.jobTitle}`}
                      </Caption1>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <Caption1 className={styles.mutedText}>
          Search your Entra ID directory, or type an email address directly.
        </Caption1>
      </div>

      <div className={styles.field}>
        <Label htmlFor="role" required>
          Role
        </Label>
        <Controller
          name="role"
          control={control}
          render={({ field }) => (
            <Select
              id="role"
              data-testid="invite-user-form-role"
              value={field.value}
              onChange={(_, data) => field.onChange(data.value as TenantRole)}
              disabled={createInvitation.isPending}
            >
              {roles.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Select>
          )}
        />
      </div>

      <FormActions
        mode="create"
        testIdPrefix="invite-user-form"
        isSubmitting={createInvitation.isPending}
        onCancel={onCancel}
        submitLabel="Send Invitation"
        submittingLabel="Sending..."
        submitIcon={<SendRegular />}
      />
    </form>
  );
};

// InviteUserForm doesn't accept an `onDirtyChange` prop today (its Dialog is dismissed with a
// plain X / backdrop click in both hosting pages, no discard-confirm) — pass a no-op so
// `useReportFormDirty` still centralises the `formState.isDirty` subscription for future wiring.
function onDirtyChangeNoop(): void {
  // intentionally empty
}
